if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
        this.vlxInitialVariantMediaSkipped = false;
        this.isInitialized = false;
        this.isInitializing = false;
        this.connectionToken = 0;
        this.mobileMediaQuery = null;
        this.mediaQueryChangeHandler = null;
        this.galleryClickHandler = this.handleGalleryClick.bind(this);
        this.galleryOpenContext = null;
        this.pendingMediaLayoutAfterClose = false;
        this.isTearingDown = false;
      }

      connectedCallback() {
        this.isTearingDown = false;
        const connectionToken = ++this.connectionToken;
        FoxTheme.Motion.inView(this, () => {
          if (!this.isConnected || connectionToken !== this.connectionToken) return;
          this.init();
        });
      }

      disconnectedCallback() {
        this.isTearingDown = true;
        this.connectionToken += 1;

        if (this.mobileMediaQuery && this.mediaQueryChangeHandler) {
          if (this.mobileMediaQuery.removeEventListener) {
            this.mobileMediaQuery.removeEventListener('change', this.mediaQueryChangeHandler);
          } else if (this.mobileMediaQuery.removeListener) {
            this.mobileMediaQuery.removeListener(this.mediaQueryChangeHandler);
          }
        }
        this.removeEventListener('click', this.galleryClickHandler);

        const lightbox = this.lightbox;
        if (lightbox) {
          if (lightbox.pswp) lightbox.pswp.close();
          lightbox.destroy();
        }
        this.destroySlider();

        this.lightbox = null;
        this.elements = null;
        this.mobileMediaQuery = null;
        this.mediaQueryChangeHandler = null;
        this.galleryOpenContext = null;
        this.pendingMediaLayoutAfterClose = false;
        this.isInitialized = false;
        this.isInitializing = false;
        this.vlxInitialVariantMediaSkipped = false;
        this.removeAttribute('data-vlx-gallery-ready');
        const galleryButton = this.querySelector('[data-vlx-gallery-more]');
        if (galleryButton) galleryButton.hidden = true;
      }

      init() {
        if (this.isInitialized || this.isInitializing || !this.isConnected) return;

        this.isInitializing = true;
        try {
          this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
          this.mediaLayout = this.dataset.mediaLayout;
          this.onlyImage = this.dataset.onlyImage === 'true';
          this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
          this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
          this.enableImageZoom = this.dataset.enableImageZoom === 'true';
          this.context = this.dataset.context;
          this.setSliderOptions();

          this.mobileMediaQuery = window.matchMedia(FoxTheme.config.mediaQueryMobile);
          this.mediaQueryChangeHandler = this.updateMediaLayout.bind(this);
          if (this.mobileMediaQuery.addEventListener) {
            this.mobileMediaQuery.addEventListener('change', this.mediaQueryChangeHandler);
          } else if (this.mobileMediaQuery.addListener) {
            this.mobileMediaQuery.addListener(this.mediaQueryChangeHandler);
          }
          this.addEventListener('click', this.galleryClickHandler);

          if (this.enableImageZoom) {
            try {
              this.initImageZoom();
            } catch (error) {
              this.lightbox = null;
            }
          }

          this.isInitialized = true;
          this.updateMediaLayout();
        } finally {
          this.isInitializing = false;
        }
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);
        const mediaItemsCount = this.querySelectorAll('.product__media-item').length;
        const enableLoop = mediaItemsCount > 1;

        this.sliderOptions = {
          init: false,
          slidesPerView: 1,
          spaceBetween: mediaItemGapMobile,
          loop: enableLoop,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal',
            },
          },
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap,
              },
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5,
              },
              1536: {
                slidesPerView: 7,
              },
            };
            break;
        }
      }

      updateMediaLayout() {
        if (!this.elements) return;

        const isMobile = this.mobileMediaQuery ? this.mobileMediaQuery.matches : FoxTheme.config.mqlMobile;
        const galleryButton = this.querySelector('[data-vlx-gallery-more]');
        const canUseDesktopGallery = this.canUseDesktopGallery();

        this.removeAttribute('data-vlx-gallery-ready');
        if (galleryButton) galleryButton.hidden = true;

        if (this.galleryOpenContext?.isDesktop && this.lightbox?.pswp && isMobile) {
          this.pendingMediaLayoutAfterClose = true;
          this.lightbox.pswp.close();
          return;
        }

        if (!isMobile && canUseDesktopGallery) {
          this.setAttribute('data-vlx-gallery-ready', 'true');
          galleryButton.hidden = false;
        }

        if (this.dataset.heroGrid === 'true' && !isMobile) {
          this.destroySlider();
          return;
        }

        if (isMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (this.sliderInstance && typeof this.sliderInstance === 'object' && this.sliderInstance.slider) {
          this.sliderInstance.slider.destroy();
        }
        this.sliderInstance = false;

        if (this.thumbsInstance && typeof this.thumbsInstance === 'object' && this.thumbsInstance.slider) {
          this.thumbsInstance.slider.destroy();
        }
        this.thumbsInstance = false;
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        const mediaNodes = this.getLiveMediaNodes();
        const dataSource = mediaNodes.map((media) => {
          const mediaType = media.dataset.mediaType;
          if (mediaType === 'model') {
            const model = media.querySelector('product-model');
            return {
              id: media.dataset.mediaIndex,
              html: model ? `<div class="pswp__item--model">${model.outerHTML}</div>` : '',
              mediaType,
            };
          }
          if (mediaType === 'video' || mediaType === 'external_video') {
            const video = media.querySelector('video-element');
            return {
              id: media.dataset.mediaIndex,
              html: video ? `<div class="pswp__item--${mediaType}">${video.outerHTML}</div>` : '',
              mediaType,
            };
          }
          if (mediaType === 'image') {
            return {
              id: media.dataset.mediaIndex,
              src: media.dataset.src,
              width: media.dataset.pswpWidth,
              height: media.dataset.pswpHeight,
              mediaType,
            };
          }
          return null;
        }).filter(Boolean);

        const lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });
        this.lightbox = lightbox;

        lightbox.addFilter('thumbEl', (thumbEl, data, index) => {
          const context = this.galleryOpenContext;
          if (context?.isDesktop && context.lightbox === lightbox) {
            return context.trigger?.matches('.js-photoswipe--zoom')
              ? context.mediaNodes[index]?.querySelector('img') || null
              : null;
          }

          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const image = slides[activeIndex].querySelector('img');
              if (image) return image;
            }
          }
          return thumbEl;
        });

        lightbox.addFilter('placeholderSrc', (placeholderSrc, { data }, index) => {
          const context = this.galleryOpenContext;
          if (context?.isDesktop && context.lightbox === lightbox) {
            return context.dataSource[index]?.msrc || placeholderSrc;
          }

          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const image = slides[activeIndex].querySelector('img');
              if (image) return image.src;
            }
          }
          return placeholderSrc;
        });

        let lastLightboxIndex = null;
        lightbox.on('change', () => {
          if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia(this);
          if (this.galleryOpenContext?.isDesktop && this.galleryOpenContext.lightbox === lightbox) {
            this.updateDesktopGalleryUi(lightbox.pswp);
          }
        });

        lightbox.on('close', () => {
          if (lightbox.pswp && !(this.galleryOpenContext?.isDesktop && this.galleryOpenContext.lightbox === lightbox)) {
            lastLightboxIndex = lightbox.pswp.currIndex;
          }
        });

        lightbox.on('destroy', () => {
          const context = this.galleryOpenContext;
          if (context?.isDesktop && context.lightbox === lightbox) {
            this.restorePresentationOptions(lightbox.options, context.savedOptions);
            this.galleryOpenContext = null;

            if (!this.isTearingDown) this.focusGalleryTrigger(context.trigger);
            if (this.pendingMediaLayoutAfterClose) {
              this.pendingMediaLayoutAfterClose = false;
              window.requestAnimationFrame(() => {
                if (this.isConnected) this.updateMediaLayout();
              });
            }
          } else if (this.sliderInstance && this.sliderInstance.slider && lastLightboxIndex !== null) {
            const slider = this.sliderInstance.slider;
            const targetIndex = lastLightboxIndex;
            const currentIndex = typeof slider.realIndex === 'number' ? slider.realIndex : slider.activeIndex;

            if (targetIndex >= 0 && currentIndex !== targetIndex) {
              window.requestAnimationFrame(() => {
                if (!this.isConnected || !this.sliderInstance?.slider) return;
                if (slider.params.loop && typeof slider.slideToLoop === 'function') {
                  slider.slideToLoop(targetIndex, 0, false);
                } else {
                  slider.slideTo(targetIndex, 0, false);
                }
              });
            }
            lastLightboxIndex = null;
          }
        });

        lightbox.on('pointerDown', (event) => {
          if (lightbox.pswp?.currSlide?.data?.mediaType !== 'image') event.preventDefault();
        });

        lightbox.on('uiRegister', () => {
          const pswp = lightbox.pswp;
          const context = this.galleryOpenContext;
          if (context?.isDesktop && context.lightbox === lightbox) {
            pswp.element.classList.add('vlx-pdp-desktop-gallery');
            pswp.element.setAttribute('role', 'dialog');
            pswp.element.setAttribute('aria-modal', 'true');
            pswp.element.setAttribute('aria-label', this.dataset.vlxGalleryLabel || '');

            pswp.ui.registerElement({
              name: 'vlx-gallery-prev',
              ariaLabel: this.dataset.vlxPreviousLabel,
              order: 1,
              isButton: true,
              appendTo: 'root',
              html: '<svg aria-hidden="true" focusable="false" viewBox="0 0 20 20"><path d="M12.5 3.5 6 10l6.5 6.5" /></svg>',
              onInit: (element) => {
                context.previousButton = element;
              },
              onClick: (event, element, instance) => instance.prev(),
            });
            pswp.ui.registerElement({
              name: 'vlx-gallery-next',
              ariaLabel: this.dataset.vlxNextLabel,
              order: 2,
              isButton: true,
              appendTo: 'root',
              html: '<svg aria-hidden="true" focusable="false" viewBox="0 0 20 20"><path d="m7.5 3.5 6.5 6.5-6.5 6.5" /></svg>',
              onInit: (element) => {
                context.nextButton = element;
              },
              onClick: (event, element, instance) => instance.next(),
            });
            pswp.ui.registerElement({
              name: 'vlx-gallery-close',
              ariaLabel: this.dataset.vlxCloseLabel,
              order: 3,
              isButton: true,
              appendTo: 'root',
              html: '<svg aria-hidden="true" focusable="false" viewBox="0 0 20 20"><path d="m5 5 10 10M15 5 5 15" /></svg>',
              onInit: (element) => {
                context.closeButton = element;
              },
              onClick: (event, element, instance) => instance.close(),
            });
            pswp.ui.registerElement({
              name: 'vlx-gallery-thumbnails',
              className: 'vlx-pdp-gallery__thumbs',
              appendTo: 'root',
              isButton: false,
              onInit: (element, instance) => {
                const track = document.createElement('div');
                track.className = 'vlx-pdp-gallery__thumbs-track';
                const buttons = context.dataSource.map((image, index) => {
                  const button = document.createElement('button');
                  button.type = 'button';
                  button.className = 'vlx-pdp-gallery__thumb';
                  button.setAttribute('aria-label', this.formatGalleryLabel(this.dataset.vlxChooseLabel, index + 1));
                  button.dataset.galleryIndex = String(index);

                  const thumbnail = document.createElement('img');
                  thumbnail.src = image.msrc;
                  thumbnail.alt = '';
                  thumbnail.loading = 'lazy';
                  thumbnail.draggable = false;
                  button.appendChild(thumbnail);
                  button.addEventListener('click', (event) => {
                    event.preventDefault();
                    instance.goTo(index);
                  });
                  track.appendChild(button);
                  return button;
                });
                element.appendChild(track);
                context.thumbnailTrack = track;
                context.thumbnailButtons = buttons;
                this.updateDesktopGalleryUi(instance);
              },
            });
            pswp.ui.registerElement({
              name: 'vlx-gallery-status',
              className: 'vlx-pdp-gallery__status',
              appendTo: 'root',
              isButton: false,
              onInit: (element, instance) => {
                element.setAttribute('role', 'status');
                element.setAttribute('aria-live', 'polite');
                element.setAttribute('aria-atomic', 'true');
                context.statusElement = element;
                this.updateDesktopGalleryUi(instance);
              },
            });
            return;
          }

          if (!this.onlyImage) {
            pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, element, instance) => instance.next(),
            });
            pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, element, instance) => instance.prev(),
            });
          }
          pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, element, instance) => instance.close(),
          });
        });

        lightbox.init();
      }

      getLiveMediaNodes() {
        if (!this.elements?.mediaList) return [];
        return [...this.elements.mediaList.children].filter(
          (media) => media.matches('.product__media-item') && !media.classList.contains('swiper-slide-duplicate')
        );
      }

      canUseDesktopGallery() {
        if (
          this.context !== 'product' ||
          this.dataset.vlxGalleryEligible !== 'true' ||
          this.dataset.heroGrid !== 'true' ||
          !this.enableImageZoom ||
          !this.querySelector('[data-vlx-gallery-more]') ||
          typeof this.lightbox?.loadAndOpen !== 'function'
        ) {
          return false;
        }

        const requiredLabels = [
          this.dataset.vlxGalleryLabel,
          this.dataset.vlxPreviousLabel,
          this.dataset.vlxNextLabel,
          this.dataset.vlxCloseLabel,
          this.dataset.vlxChooseLabel,
          this.dataset.vlxPositionTemplate,
        ];
        if (requiredLabels.some((label) => !label)) return false;

        const mediaNodes = this.getLiveMediaNodes();
        if (mediaNodes.length <= 3) return false;

        const mediaIds = new Set();
        return mediaNodes.every((media) => {
          const id = media.dataset.mediaId;
          const width = Number(media.dataset.pswpWidth);
          const height = Number(media.dataset.pswpHeight);
          const isValid =
            media.dataset.mediaType === 'image' &&
            Boolean(id) &&
            !mediaIds.has(id) &&
            Boolean(media.dataset.src) &&
            Boolean(media.dataset.vlxThumbnailSrc) &&
            Number.isFinite(width) &&
            width > 0 &&
            Number.isFinite(height) &&
            height > 0;
          if (id) mediaIds.add(id);
          return isValid;
        });
      }

      buildDesktopGallerySnapshot() {
        const mediaNodes = this.getLiveMediaNodes();
        const mediaIds = new Set();
        const dataSource = [];
        for (const media of mediaNodes) {
          const id = media.dataset.mediaId;
          const width = Number(media.dataset.pswpWidth);
          const height = Number(media.dataset.pswpHeight);
          const image = media.querySelector('img');
          if (
            media.dataset.mediaType !== 'image' ||
            !id ||
            mediaIds.has(id) ||
            !media.dataset.src ||
            !media.dataset.vlxThumbnailSrc ||
            !Number.isFinite(width) ||
            width <= 0 ||
            !Number.isFinite(height) ||
            height <= 0
          ) {
            return null;
          }
          mediaIds.add(id);
          dataSource.push({
            id,
            src: media.dataset.src,
            width,
            height,
            alt: image?.getAttribute('alt')?.trim() || this.formatGalleryPosition(dataSource.length + 1, mediaNodes.length),
            msrc: media.dataset.vlxThumbnailSrc,
            mediaType: 'image',
          });
        }
        return { mediaNodes, dataSource };
      }

      formatGalleryLabel(template, index) {
        return String(template || '').replace('__INDEX__', String(index));
      }

      formatGalleryPosition(index, total) {
        return String(this.dataset.vlxPositionTemplate || '')
          .replace('__INDEX__', String(index))
          .replace('__TOTAL__', String(total));
      }

      savePresentationOptions(options) {
        const optionNames = [
          'dataSource',
          'loop',
          'paddingFn',
          'showHideAnimationType',
          'showAnimationDuration',
          'hideAnimationDuration',
          'returnFocus',
          'zoom',
          'imageClickAction',
          'tapAction',
          'doubleTapAction',
        ];
        return optionNames.map((name) => ({
          name,
          hadOwnValue: Object.prototype.hasOwnProperty.call(options, name),
          value: options[name],
        }));
      }

      restorePresentationOptions(options, savedOptions) {
        if (!options || !savedOptions) return;
        savedOptions.forEach(({ name, hadOwnValue, value }) => {
          if (hadOwnValue) {
            options[name] = value;
          } else {
            delete options[name];
          }
        });
      }

      openDesktopGallery(trigger) {
        if (!this.canUseDesktopGallery() || this.mobileMediaQuery?.matches) return false;
        if (this.galleryOpenContext?.isDesktop) return false;
        const snapshot = this.buildDesktopGallerySnapshot();
        if (!snapshot || snapshot.dataSource.length <= 3) return false;

        const isMoreButton = trigger.hasAttribute('data-vlx-gallery-more');
        const targetId = isMoreButton
          ? snapshot.mediaNodes[3]?.dataset.mediaId
          : trigger.dataset.mediaId;
        const targetIndex = snapshot.dataSource.findIndex((image) => image.id === targetId);
        if (targetIndex < 0) return false;

        const lightbox = this.lightbox;
        const context = {
          isDesktop: true,
          lightbox,
          trigger,
          mediaNodes: snapshot.mediaNodes,
          dataSource: snapshot.dataSource,
          savedOptions: this.savePresentationOptions(lightbox.options),
          thumbnailButtons: [],
        };
        context.dataSource = snapshot.dataSource.map((image) => ({ ...image }));
        this.galleryOpenContext = context;

        const options = lightbox.options;
        options.loop = false;
        options.paddingFn = () => {
          const shortViewport = window.innerHeight < 600;
          const sidePadding = Math.max(72, (window.innerWidth - 1200) / 2);
          return {
            top: shortViewport ? 56 : 72,
            right: sidePadding,
            bottom: shortViewport ? 88 : 112,
            left: sidePadding,
          };
        };
        options.showHideAnimationType = 'fade';
        options.showAnimationDuration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 180;
        options.hideAnimationDuration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 180;
        options.returnFocus = false;
        options.zoom = false;
        options.imageClickAction = 'zoom';
        options.tapAction = 'zoom';
        options.doubleTapAction = 'zoom';

        try {
          const opened = lightbox.loadAndOpen(targetIndex, context.dataSource);
          if (opened === false) {
            this.restorePresentationOptions(options, context.savedOptions);
            this.galleryOpenContext = null;
            return false;
          }
          return true;
        } catch (error) {
          this.restorePresentationOptions(options, context.savedOptions);
          this.galleryOpenContext = null;
          return false;
        }
      }

      openLegacyGallery(trigger) {
        if (!this.lightbox || trigger.dataset.mediaType !== 'image') return false;
        const index = Number(trigger.dataset.mediaIndex);
        if (!Number.isFinite(index) || index < 0) return false;
        try {
          return this.lightbox.loadAndOpen(index) !== false;
        } catch (error) {
          return false;
        }
      }

      handleGalleryClick(event) {
        const clickedElement = event.target instanceof Element ? event.target : event.target?.parentElement;
        if (!clickedElement) return;

        const moreButton = clickedElement.closest('[data-vlx-gallery-more]');
        if (moreButton && this.contains(moreButton)) {
          event.preventDefault();
          this.openDesktopGallery(moreButton);
          return;
        }

        const zoomButton = clickedElement.closest('.js-photoswipe--zoom');
        if (!zoomButton || !this.contains(zoomButton) || zoomButton.dataset.mediaType !== 'image') return;
        event.preventDefault();
        if (this.dataset.vlxGalleryReady === 'true' && !this.mobileMediaQuery?.matches) {
          this.openDesktopGallery(zoomButton);
          return;
        }
        this.openLegacyGallery(zoomButton);
      }

      updateDesktopGalleryUi(pswp) {
        const context = this.galleryOpenContext;
        if (!context?.isDesktop || context.lightbox?.pswp !== pswp || !pswp) return;

        const activeIndex = Math.max(0, Math.min(pswp.currIndex || 0, context.dataSource.length - 1));
        if (context.previousButton) context.previousButton.disabled = activeIndex === 0;
        if (context.nextButton) context.nextButton.disabled = activeIndex === context.dataSource.length - 1;
        context.thumbnailButtons?.forEach((button, index) => {
          if (index === activeIndex) {
            button.setAttribute('aria-current', 'true');
          } else {
            button.removeAttribute('aria-current');
          }
        });
        if (context.statusElement) {
          context.statusElement.textContent = this.formatGalleryPosition(activeIndex + 1, context.dataSource.length);
        }

        const activeThumbnail = context.thumbnailButtons?.[activeIndex];
        const track = context.thumbnailTrack;
        if (activeThumbnail && track) {
          const trackBounds = track.getBoundingClientRect();
          const activeBounds = activeThumbnail.getBoundingClientRect();
          if (activeBounds.left < trackBounds.left) {
            track.scrollLeft -= trackBounds.left - activeBounds.left;
          } else if (activeBounds.right > trackBounds.right) {
            track.scrollLeft += activeBounds.right - trackBounds.right;
          }
        }
      }

      focusGalleryTrigger(trigger) {
        if (!this.isConnected) return;
        let focusTarget = trigger;
        const canFocusTrigger =
          focusTarget?.isConnected && focusTarget.getClientRects().length > 0 && !focusTarget.disabled;
        if (!canFocusTrigger) {
          focusTarget = this.querySelector('[data-vlx-gallery-more]:not([hidden])');
        }
        if (!focusTarget || focusTarget.getClientRects().length === 0) {
          focusTarget = [...this.querySelectorAll('.js-photoswipe--zoom')].find((button) => button.getClientRects().length > 0);
        }
        if (!focusTarget) return;
        try {
          focusTarget.focus({ preventScroll: true });
        } catch (error) {
          focusTarget.focus();
        }
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            this.updateGalleryCounter(swiper);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;
          const syncIndex = typeof swiper.realIndex === 'number' ? swiper.realIndex : activeIndex;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(syncIndex);
          }

          if (slides[activeIndex]) {
            this.updateGalleryCounter(swiper);
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      updateGalleryCounter(swiper) {
        const counter = this.querySelector('.product__media-gallery-counter');
        if (!counter || !swiper) return;

        const current = counter.querySelector('.current-slide');
        const total = counter.querySelector('.total-slides');
        const totalSlides =
          this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)').length || swiper.slides.length;
        const activeSlide = (typeof swiper.realIndex === 'number' ? swiper.realIndex : swiper.activeIndex) + 1;

        if (current) current.textContent = activeSlide;
        if (total && totalSlides) total.textContent = totalSlides;
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      getFeaturedMedia(variant) {
        return (
          variant?.featured_media ||
          (variant?.featured_image
            ? {
                id: variant.featured_image.id,
                position: variant.featured_image.position || 0,
                media_type: 'image',
                src: variant.featured_image.src,
              }
            : null)
        );
      }

      getMediaSrcKey(src) {
        if (!src) return '';

        try {
          const mediaUrl = new URL(src, window.location.origin);
          return decodeURIComponent(mediaUrl.pathname.split('/').pop() || '')
            .toLowerCase()
            .replace(/_(pico|icon|thumb|small|compact|medium|large|grande|master|[0-9]+x[0-9]*|x[0-9]+)(?=\.)/, '');
        } catch (error) {
          return '';
        }
      }

      findMediaItemForFeaturedMedia(featuredMedia) {
        if (!featuredMedia) return null;

        const featuredMediaSrcKey = this.getMediaSrcKey(
          featuredMedia.src || featuredMedia.preview_image?.src || featuredMedia.url
        );

        return (
          this.elements.mediaItems.find((media) => media.dataset.mediaId == featuredMedia.id) ||
          this.elements.mediaItems.find((media) => {
            const mediaSrcKey = this.getMediaSrcKey(media.dataset.src || media.querySelector('img')?.src);
            return featuredMediaSrcKey && mediaSrcKey === featuredMediaSrcKey;
          }) ||
          null
        );
      }

      getSlug(value) {
        return String(value || '')
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/ä/g, 'ae')
          .replace(/ö/g, 'oe')
          .replace(/ü/g, 'ue')
          .replace(/ß/g, 'ss')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }

      findMediaItemForVariantColor(variant) {
        const color = variant?.option1 || variant?.options?.[0];
        const colorSlug = this.getSlug(color);
        if (!colorSlug) return null;

        return (
          this.elements.mediaItems.find((media) => {
            const mediaSrcKey = this.getMediaSrcKey(media.dataset.src || media.querySelector('img')?.src);
            return mediaSrcKey.includes(`-${colorSlug}-front.`);
          }) ||
          this.elements.mediaItems.find((media) => {
            const mediaSrcKey = this.getMediaSrcKey(media.dataset.src || media.querySelector('img')?.src);
            return mediaSrcKey.includes(`-${colorSlug}-`);
          }) ||
          null
        );
      }

      setActiveMedia(variant) {
        if (!variant) return;

        const hasVariantUrl = new URLSearchParams(window.location.search).has('variant');
        if (
          this.dataset.vlxFirstMediaOnLoad === 'true' &&
          this.context === 'product' &&
          !this.vlxInitialVariantMediaSkipped &&
          !hasVariantUrl
        ) {
          this.vlxInitialVariantMediaSkipped = true;
          return;
        }

        this.vlxInitialVariantMediaSkipped = true;

        const featuredMedia = this.getFeaturedMedia(variant);
        if (!featuredMedia) return;

        const selectedMedia =
          this.findMediaItemForFeaturedMedia(featuredMedia) || this.findMediaItemForVariantColor(variant);
        if (this.sliderInstance.slider) {
          const slideIndex = selectedMedia
            ? Number(selectedMedia.dataset.mediaIndex)
            : Math.min(this.elements.mediaItems.length - 1, Math.max(0, (featuredMedia.position || 1) - 1));
          if (this.sliderInstance.slider.params.loop && typeof this.sliderInstance.slider.slideToLoop === 'function') {
            this.sliderInstance.slider.slideToLoop(slideIndex, 0, false);
          } else {
            this.sliderInstance.slider.slideTo(slideIndex, 0, false);
          }
        } else {
          this.sortMediaItems(variant, featuredMedia);
        }
      }

      sortMediaItems(variant, featuredMedia) {
        if (!featuredMedia) featuredMedia = this.getFeaturedMedia(variant);
        if (!featuredMedia) return;

        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        const selectedMedia =
          this.findMediaItemForFeaturedMedia(featuredMedia) || this.findMediaItemForVariantColor(variant);
        newMedias.some((media, index) => {
          if (media === selectedMedia) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const sortedSelectedMedia =
            this.findMediaItemForFeaturedMedia(featuredMedia) || this.findMediaItemForVariantColor(variant);
          if (sortedSelectedMedia) window.scrollTo({ top: sortedSelectedMedia.offsetTop, behavior: 'smooth' });
        }
      }
    }
  );
}
